import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { AsyncPipe } from '@angular/common';
import { Component, inject, OnInit, ViewChild } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { DataConnect } from '@angular/fire/data-connect';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSidenav, MatSidenavModule } from '@angular/material/sidenav';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTreeModule } from '@angular/material/tree';
import { ActivatedRoute, NavigationEnd, Router, RouterModule } from '@angular/router';
import { AuthService, ContainerDirective, DialogService, MessagingService, RightsService } from '@copilot/shared';
import { getOrganization, GetOrganizationData, RightType } from '@dataconnect/copilot';
import type { DataConnect as FirebaseDataConnect } from '@firebase/data-connect';
import { map as _map, get, isEmpty } from 'lodash-es';
import { BehaviorSubject, from, Observable } from 'rxjs';
import { filter, map, shareReplay, switchMap, tap } from 'rxjs/operators';

interface OrganizationNode {
  id: string;
  authId: string;
  uiId: string;
  name: string;
  thumbnail?: string;
  children: Observable<OrganizationNode[]>;
}

@Component({
  imports: [
    RouterModule,
    MatIconModule,
    MatButtonModule,
    MatToolbarModule,
    MatSidenavModule,
    MatListModule,
    MatTreeModule,
    MatProgressSpinnerModule,
    AsyncPipe,
    ContainerDirective
  ],
  selector: 'app-root',
  templateUrl: './app.html',
  styleUrl: './app.scss',
})
export class App implements OnInit {
  @ViewChild('drawer') drawer!: MatSidenav;

  protected title = 'copilot';
  private breakpointObserver = inject(BreakpointObserver);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private authService = inject(AuthService);
  private messagingService = inject(MessagingService);
  public rightsService = inject(RightsService);
  public dialogService = inject(DialogService);
  private dataConnect = inject(DataConnect);

  // injectGetOrganization({ authorizedId: route.paramMap.get('authId')!, id: route.paramMap.get('orgId')! }), 'organizations.0')

  public isHandset$ = this.breakpointObserver.observe(Breakpoints.Handset);
  public organization: GetOrganizationData['organizations'][0] | null = null;
  public authId = '';
  public RightType = RightType;

  // Tree data source
  public orgTreeDataSource!: BehaviorSubject<OrganizationNode[]>;
  public expandedNodes = new Set<string>();
  public leafNodes = new Set<string>();

  // Accessor functions for the tree
  public childrenAccessor = (node: OrganizationNode): Observable<OrganizationNode[]> => {
    return node.children;
  };

  private topLevelNodes = toObservable(this.rightsService.rights).pipe(
    map(rights => rights.map(org => ({
      id: org.organization.id,
      name: org.organization.name,
      thumbnail: get(org.organization, 'logo', '') as string,
      authId: org.organization.id,
      uiId: `${org.organization.id}-${org.organization.id}`,
      children: this.loadChildrenForOrganization(org.organization.id, org.organization.id)
    }))),
    shareReplay(1),
  );

  onToggleNode(node: OrganizationNode): void {
    const isExpanding = !this.expandedNodes.has(node.uiId);
    if (isExpanding){
      this.expandedNodes.add(node.uiId);
    } else {
      this.expandedNodes.delete(node.uiId);
    }
  }

  trackByNode = (index: number, node: OrganizationNode): string => node.uiId;

  // istanbul ignore next - not easily testable, also wait until lazy loading is working
  private loadChildrenForOrganization(id: string, authId: string): Observable<OrganizationNode[]> {
    // let expanded = false;
    const dataConnect = this.dataConnect as unknown as FirebaseDataConnect;
    const query = from(getOrganization(dataConnect, {
      id: id,
      authorizedId: authId,
    })).pipe(
      map(({ data }) => _map(get(data, 'organizations.0.children'), (org) => ({
        id: org.id,
        authId: authId,
        uiId: `${authId}-${org.id}`,
        name: org.name,
        thumbnail: org.logo || '',
        children: this.loadChildrenForOrganization(org.id, authId)
      }))),
      tap(children => {
        if (isEmpty(children)) {
          this.leafNodes.add(`${authId}-${id}`);
        }
      }),
      shareReplay(1)
    );

    // Issues with lazy loading, just load it all
    return query;
    // return new Observable<OrganizationNode[]>(subscriber => {
    //   expanded ||= this.expandedNodes.has(id);
    //   if (expanded) {
    //     query.subscribe(subscriber);
    //   } else {
    //     subscriber.next([]);
    //     subscriber.complete();
    //   }
    // });
  }

  async ngOnInit(): Promise<void> {
    // Initialize tree data source in ngOnInit to avoid injection context issues
    this.orgTreeDataSource = new BehaviorSubject<OrganizationNode[]>([{
      id: 'root',
      uiId: 'root',
      authId: '',
      name: 'Organizations',
      children: this.topLevelNodes,
    }]);
    try {
      await this.messagingService.initialize(false);
    } catch (error) {
      this.dialogService.error($localize`Failed to initialise push notifications.`, error);
    }
  }

  public hideHeader$ = this.router.events.pipe(
    filter(event => event instanceof NavigationEnd),
    map(() => this.activatedRoute),
    map(route => {
      while (route.firstChild) {
        route = route.firstChild;
      }
      this.authId = get(route.snapshot.params, 'authId', '');
      return route;
    }),
    switchMap(route => route.data),
    tap(data => this.organization = get(data, 'organization')),
    map(data => data['hideHeader'] === true)
  );

  public toggleMenu(): void {
    this.drawer.toggle();
  }

  public logout(): void {
    this.authService.signOut();
  }
}
